import { createMutationId } from "./equipmentConcurrency";

// A UUID identifies immutable image bytes in mobile caches. Allocate it when
// selecting a replacement, then keep that selection unchanged during save retries.
export const selectInspectionImage = (currentImageUuid, file) => ({
  file: file || null,
  uuid: file ? createMutationId() : currentImageUuid || null,
});

export const PHOTO_POLICY = Object.freeze({
  version: "web-detail-v1", maxSourceBytes: 25 * 1024 * 1024,
  maxPixels: 40_000_000, maxEdge: 2560, quality: 0.85, maxUploadBytes: 10 * 1024 * 1024,
});

const invalidImage = () => new Error("Choose a valid, non-animated JPEG, PNG or WebP photo.");
const ascii = (bytes, offset, length) => String.fromCharCode(...bytes.subarray(offset, offset + length));

// Inspect dimensions before decoding: compressed images must not cause unbounded
// bitmap allocations. Trust signatures and container dimensions, not file names.
export function inspectPhotoHeader(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let width; let height; let format;
  if (bytes.length >= 24 && ascii(bytes, 0, 8) === "\x89PNG\r\n\x1a\n" && ascii(bytes, 12, 4) === "IHDR") {
    format = "png"; width = view.getUint32(16); height = view.getUint32(20);
    for (let offset = 8; offset + 12 <= bytes.length;) {
      const length = view.getUint32(offset);
      if (ascii(bytes, offset + 4, 4) === "acTL") throw invalidImage();
      if (length > bytes.length - offset - 12) throw invalidImage();
      offset += length + 12;
    }
  } else if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    format = "jpeg";
    for (let offset = 2; offset + 3 < bytes.length;) {
      if (bytes[offset++] !== 0xff) throw invalidImage();
      while (bytes[offset] === 0xff) offset++;
      const marker = bytes[offset++];
      if (marker === 0xd9 || marker === 0xda) break;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > bytes.length) throw invalidImage();
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) throw invalidImage();
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker)) {
        if (length < 8) throw invalidImage();
        height = view.getUint16(offset + 3); width = view.getUint16(offset + 5); break;
      }
      offset += length;
    }
  } else if (bytes.length >= 30 && ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP") {
    format = "webp";
    const chunk = ascii(bytes, 12, 4);
    if (chunk === "VP8X") {
      if (bytes[20] & 2) throw invalidImage();
      width = 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16);
      height = 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16);
    } else if (chunk === "VP8 " && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
      width = view.getUint16(26, true) & 0x3fff; height = view.getUint16(28, true) & 0x3fff;
    } else if (chunk === "VP8L" && bytes[20] === 0x2f) {
      const bits = view.getUint32(21, true);
      width = 1 + (bits & 0x3fff); height = 1 + ((bits >>> 14) & 0x3fff);
    }
  }
  if (!width || !height || !format) throw invalidImage();
  if (width * height > PHOTO_POLICY.maxPixels) throw new Error("This photo exceeds 40 megapixels. Choose a smaller copy.");
  return { width, height, format };
}

function checkCancelled(signal) {
  if (signal?.aborted) throw new DOMException("Photo preparation cancelled", "AbortError");
}

const readBytes = (file) => typeof file.arrayBuffer === "function" ? file.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result); reader.onerror = () => reject(invalidImage());
  reader.readAsArrayBuffer(file);
});

async function decodePhoto(file) {
  if (typeof createImageBitmap === "function") return createImageBitmap(file, { imageOrientation: "from-image" });
  const url = URL.createObjectURL(file);
  try {
    const image = new Image(); image.src = url; await image.decode(); return image;
  } finally { URL.revokeObjectURL(url); }
}

// Native decode/toBlob cannot be cancelled reliably. Keep the slot until their
// actual completion and cleanup, including across dialog unmount/remount. Only
// the newest waiting selection is retained; cancelled selections never read bytes.
let preparationActive = false;
let pendingPreparation = null;

function cancelPendingPreparation(job) {
  if (pendingPreparation !== job) return;
  pendingPreparation = null;
  job.options.signal?.removeEventListener("abort", job.cancel);
  job.file = null;
  job.options = null;
  job.reject(new DOMException("Photo preparation cancelled", "AbortError"));
}

function startPendingPreparation() {
  if (preparationActive || !pendingPreparation) return;
  const job = pendingPreparation;
  pendingPreparation = null;
  job.options.signal?.removeEventListener("abort", job.cancel);
  preparationActive = true;
  const finish = () => {
    job.file = null;
    job.options = null;
    preparationActive = false;
    startPendingPreparation();
  };
  processInspectionImage(job.file, job.options).then(
    result => { finish(); job.resolve(result); },
    error => { finish(); job.reject(error); }
  );
}

export function prepareInspectionImage(file, options = {}) {
  return new Promise((resolve, reject) => {
    checkCancelled(options.signal);
    if (pendingPreparation) cancelPendingPreparation(pendingPreparation);
    const job = { file, options, resolve, reject };
    job.cancel = () => cancelPendingPreparation(job);
    pendingPreparation = job;
    options.signal?.addEventListener("abort", job.cancel, { once: true });
    startPendingPreparation();
  });
}

async function processInspectionImage(file, { signal, decode = decodePhoto, createCanvas = () => document.createElement("canvas") } = {}) {
  checkCancelled(signal);
  if (!file?.size) throw invalidImage();
  if (file.size > PHOTO_POLICY.maxSourceBytes) throw new Error("Choose a photo smaller than 25 MB.");
  const header = inspectPhotoHeader(await readBytes(file));
  checkCancelled(signal);
  let bitmap; let canvas;
  try {
    bitmap = await decode(file);
    checkCancelled(signal);
    const width = bitmap.width || bitmap.naturalWidth; const height = bitmap.height || bitmap.naturalHeight;
    if (!width || !height || width * height > PHOTO_POLICY.maxPixels || width * height !== header.width * header.height) throw invalidImage();
    const scale = Math.min(1, PHOTO_POLICY.maxEdge / Math.max(width, height));
    if (header.format === "jpeg" && scale === 1 && file.size <= PHOTO_POLICY.maxUploadBytes) {
      // Already bounded JPEGs need no lossy browser pass. The server still
      // validates, normalizes orientation, strips metadata and canonicalizes.
      return Object.freeze({ file: file.type === "image/jpeg" ? file : new File([file], file.name || "photo.jpg", { type: "image/jpeg", lastModified: file.lastModified || 0 }), sourceBytes: file.size, width, height, policy: PHOTO_POLICY.version });
    }
    canvas = createCanvas(); canvas.width = Math.max(1, Math.round(width * scale)); canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Photo preparation is unavailable in this browser. Try another browser.");
    context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", PHOTO_POLICY.quality));
    checkCancelled(signal);
    if (!blob?.size || blob.type !== "image/jpeg" || blob.size > PHOTO_POLICY.maxUploadBytes) throw new Error("Could not prepare this photo within the upload limit. Choose a smaller copy.");
    return Object.freeze({
      file: new File([blob], `${(file.name || "photo").replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg", lastModified: file.lastModified || 0 }),
      sourceBytes: file.size, width: canvas.width, height: canvas.height, policy: PHOTO_POLICY.version,
    });
  } catch (error) {
    if (error?.name === "AbortError" || error?.message?.startsWith("Could not") || error?.message?.startsWith("Photo preparation")) throw error;
    throw invalidImage();
  } finally {
    bitmap?.close?.();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
